import { Temporal } from '@js-temporal/polyfill'

/**
 * Convert PlainDate to Date.
 * Returns undefined if the input is undefined, otherwise converts PlainDate to Date at midnight local time.
 * If the input is already a Date, returns it unchanged.
 *
 * @example
 * toDate(toPlainDate('2024-01-15')) // Date at 2024-01-15 00:00:00 local time
 * toDate(new Date('2024-01-15')) // Returns the same Date
 * toDate(undefined) // undefined
 */
export function toDate(
  value: Date | Temporal.PlainDate | string | undefined | null,
): Date | undefined | null {
  if (value === null || value === undefined || value instanceof Date) {
    return value
  }

  const plainDate = typeof value === 'string' ? toPlainDate(value) : value

  if (plainDate === null) {
    return null
  }

  return new Date(plainDate.year, plainDate.month - 1, plainDate.day)
}

type MaybePlainDate = string | Date | Temporal.PlainDate | null | undefined

/**
 * Convert a string, Date, or PlainDate to a PlainDate object.
 * Returns null if the input is invalid (safe parsing).
 * If already a PlainDate, returns it unchanged.
 *
 * @example
 * toPlainDate('2024-01-15') // PlainDate(2024-01-15)
 * toPlainDate(new Date(2024, 0, 15)) // PlainDate(2024-01-15)
 * toPlainDate('invalid') // null
 * toPlainDate(Temporal.PlainDate.from('2024-01-15')) // PlainDate(2024-01-15) (passthrough)
 */
export function toPlainDate(value: MaybePlainDate): Temporal.PlainDate | null {
  if (value === null || value === undefined) {
    return null
  }

  if (value instanceof Temporal.PlainDate) {
    return value
  }

  if (typeof value === 'string') {
    const temporal = parseTemporal(value)
    if (temporal instanceof Temporal.PlainDateTime) {
      return temporal.toPlainDate()
    }

    return temporal
  }

  // value is Date
  try {
    return Temporal.PlainDate.from({
      day: value.getDate(),
      month: value.getMonth() + 1,
      year: value.getFullYear(),
    })
  } catch {
    return null
  }
}

interface SortByDateOptions<T> {
  /**
   * @default 'desc'
   */
  order?: 'asc' | 'desc'
  /**
   * Comparator applied to break ties between items with equal dates.
   * Receives the same items as the sort and returns a standard comparator number.
   */
  tieBreak?: (a: T, b: T) => number
}

/**
 * Sort an array of items by date.
 * Does not mutate the original array.
 * Items with null dates are placed at the end.
 * Items with invalid dates are also placed at the end.
 *
 * @example
 * sortByDate(
 *   [
 *     { date: '2024-01-15' },
 *     { date: '2024-01-10' },
 *     { date: null },
 *   ],
 *   (item) => item.date,
 *   { order: 'desc' },
 * ) // Returns items sorted: 2024-01-15, 2024-01-10, null
 */
export function sortByDate<T>(
  items: Array<T>,
  accessorFn: (item: T) => Parameters<typeof toPlainDate>[0],
  { order = 'desc', tieBreak }: SortByDateOptions<T> = {},
): Array<T> {
  return items.slice().sort((a, b) => {
    const aDate = accessorFn(a)
    const bDate = accessorFn(b)

    const aPlainDate = aDate === null ? null : toPlainDate(aDate)
    const bPlainDate = bDate === null ? null : toPlainDate(bDate)

    // Both invalid/null - maintain order
    if (aPlainDate === null && bPlainDate === null) {
      return 0
    }

    // a is invalid - push to end
    if (aPlainDate === null) {
      return 1
    }

    // b is invalid - push to end (so a comes before b)
    if (bPlainDate === null) {
      return -1
    }

    const dateComparison =
      order === 'desc'
        ? Temporal.PlainDate.compare(bPlainDate, aPlainDate)
        : Temporal.PlainDate.compare(aPlainDate, bPlainDate)
    if (dateComparison !== 0) {
      return dateComparison
    }

    return tieBreak ? tieBreak(a, b) : 0
  })
}

interface ParseTemporalOptions {
  timeZone?: string
}

/**
 * Parse a string into a Temporal object, attempting various formats.
 * Returns null if the string cannot be parsed as any supported type.
 * This is useful when you don't know whether a timestamp includes time information or timezone.
 *
 * @param str - The date/datetime string to parse (ISO 8601 format)
 * @param options.timeZone - Time zone used when converting an instant
 * @returns PlainDateTime if the string includes time, PlainDate if only date, or null if parsing fails
 *
 * @example
 * parseTemporal('2025-05-28T06:34:18Z') // PlainDateTime(2025-05-28T06:34:18) - from UTC instant
 * parseTemporal('2024-01-15T14:30:00') // PlainDateTime(2024-01-15T14:30:00) - local datetime
 * parseTemporal('2024-01-15') // PlainDate(2024-01-15) - date only
 * parseTemporal('invalid') // null
 */
export function parseTemporal(
  str: string,
  { timeZone = Temporal.Now.timeZoneId() }: ParseTemporalOptions = {},
): Temporal.PlainDateTime | Temporal.PlainDate | null {
  // Try parsing as Instant (for strings with timezone like "2025-05-28T06:34:18Z")
  try {
    return Temporal.Instant.from(str)
      .toZonedDateTimeISO(timeZone)
      .toPlainDateTime()
  } catch {
    // do nothing
  }

  // Check if the string contains time information (has 'T' separator)
  // This helps us choose the right parser since both PlainDate.from() and PlainDateTime.from()
  // can parse each other's formats (with defaults/truncation)
  if (str.includes('T')) {
    try {
      // Try parsing as PlainDateTime (for local datetime strings like "2024-01-15T14:30:00")
      return Temporal.PlainDateTime.from(str)
    } catch {
      // do nothing
    }
  } else {
    try {
      // Try parsing as PlainDate (for date-only strings like "2024-01-15")
      return Temporal.PlainDate.from(str)
    } catch {
      // do nothing
    }
  }

  return null
}

export function comparePlainDates(
  one: MaybePlainDate,
  two: MaybePlainDate = Temporal.Now.plainDateISO(),
) {
  const onePlainDate = toPlainDate(one)
  const twoPlainDate = toPlainDate(two)

  return onePlainDate !== null && twoPlainDate !== null
    ? Temporal.PlainDate.compare(onePlainDate, twoPlainDate)
    : 0
}

export function isInFuture(value: MaybePlainDate) {
  return comparePlainDates(value) > 0
}

export function isWeekday(value: MaybePlainDate) {
  const plainDate = toPlainDate(value)
  return plainDate !== null && plainDate.dayOfWeek <= 5
}
